// Global observable for which sub-system currently owns controller
// input. Three states:
//   'menu'  (default). The focus bridge is active, d-pad / sticks
//                       drive spatialFocus moves, A activates, B backs out.
//   'board'. GameBoard's own controller handler owns all input.
//                       The focus bridge ignores events until we return to 'menu'.
//   'text'. A text input has focus. Both the focus bridge and
//                       the board handler stand down, letting the DOM (and
//                       on Steam Deck the OS OSK) handle typing.
//
// The module is a tiny singleton. GameBoard calls set('board') on
// mount; screens, modals, and text inputs adjust it as needed.

const LEGAL = new Set(['menu', 'board', 'text']);

function create() {
  let current = 'menu';
  const listeners = new Set();
  function set(next) {
    if (!LEGAL.has(next)) return;
    if (next === current) return;
    current = next;
    for (const fn of listeners) fn(current);
  }
  function get() { return current; }
  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  // DOM wiring: auto-switch to 'text' while a text-entry element is
  // focused anywhere in the document, and back to 'menu' when it
  // blurs. Safe to call multiple times. Returns an off() function.
  //
  // Also self-corrects every ~500 ms: if the mode is 'text' but the
  // currently-focused element isn't actually a text input (or the
  // input unmounted without firing blur during a route transition),
  // flip mode back. Without this, any screen that had a text input
  // focused on mount would leave the focus bridge stuck standing
  // down forever once the user navigated away.
  function installTextInputObserver(doc = typeof document !== 'undefined' ? document : null) {
    if (!doc) return () => {};
    function isTextTarget(el) {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'textarea') return true;
      if (tag === 'input') {
        const type = (el.type || 'text').toLowerCase();
        return type === 'text' || type === 'search' || type === 'email'
          || type === 'password' || type === 'tel' || type === 'url' || type === 'number';
      }
      if (el.isContentEditable) return true;
      return false;
    }
    let previous = null;
    const onFocusIn = (e) => {
      if (current === 'board') return; // board owns input; don't hijack
      if (isTextTarget(e.target)) {
        previous = current;
        set('text');
      }
    };
    const onFocusOut = (e) => {
      if (current === 'text' && isTextTarget(e.target)) {
        set(previous || 'menu');
        previous = null;
      }
    };
    // Sweep for stale 'text' mode. Cheap. Runs twice a second.
    const sweep = setInterval(() => {
      if (current !== 'text') return;
      const active = doc.activeElement;
      if (!active || !isTextTarget(active) || !doc.contains(active)) {
        set(previous || 'menu');
        previous = null;
      }
    }, 500);
    doc.addEventListener('focusin', onFocusIn, true);
    doc.addEventListener('focusout', onFocusOut, true);
    return () => {
      doc.removeEventListener('focusin', onFocusIn, true);
      doc.removeEventListener('focusout', onFocusOut, true);
      clearInterval(sweep);
    };
  }

  // Test helper.
  function _resetForTest() {
    current = 'menu';
    listeners.clear();
  }

  return { set, get, subscribe, installTextInputObserver, _resetForTest };
}

export const controllerMode = create();
export const createControllerMode = create; // for tests that want isolation
